import { makeGenerator, makeLoad } from "../sim/grid.ts";
import { makeBus, makeLine, makeNetwork } from "../sim/network.ts";
import { DEFAULT_CONSTANTS } from "../sim/types.ts";
import { emptyGrid, type GameContext } from "./state.ts";

export interface LevelDef {
  id: number;
  layer: number;
  title: string;
  subtitle: string;
  briefing: string[];
  available: boolean;
  build: () => GameContext;
}

export interface NodeLayout {
  id: string;
  kind: "gen" | "load" | "bus";
  x: number;
  y: number;
}

export const LAYOUTS: Record<number, NodeLayout[]> = {};

function layout(id: number, nodes: NodeLayout[]): NodeLayout[] {
  LAYOUTS[id] = nodes;
  return nodes;
}

// ============== Layer 1 ==============

function level1(): GameContext {
  const state = emptyGrid();
  state.generators = [
    makeGenerator({ id: "g1", name: "Coal-1",  type: "coal",       pMin: 100, pMax: 400, cost: 35,  rampRate: 8,  startupTime: 30, initialStatus: "on", initialOutput: 250 }),
    makeGenerator({ id: "g2", name: "Gas-1",   type: "gas-ccgt",   pMin: 80,  pMax: 300, cost: 55,  rampRate: 30, startupTime: 5,  initialStatus: "on", initialOutput: 150 }),
    makeGenerator({ id: "g3", name: "Peaker",  type: "gas-peaker", pMin: 20,  pMax: 120, cost: 110, rampRate: 60, startupTime: 2,  initialStatus: "off" }),
  ];
  state.loads = [makeLoad("l1", "City", 380)];
  return {
    state, constants: DEFAULT_CONSTANTS, winSeconds: 90,
    events: [
      { t: 30, label: "Evening demand surge", apply: (c) => { if (c.state.loads[0]) c.state.loads[0].baseDemand = 520; } },
    ],
  };
}

// ============== Layer 2 ==============

function level2(): GameContext {
  const state = emptyGrid();
  state.generators = [
    makeGenerator({ id: "g1", name: "Coal-1",   type: "coal",       pMin: 120, pMax: 500, cost: 32,  rampRate: 6,  startupTime: 30, initialStatus: "on", initialOutput: 350 }),
    makeGenerator({ id: "g2", name: "Gas-CCGT", type: "gas-ccgt",   pMin: 80,  pMax: 280, cost: 58,  rampRate: 25, startupTime: 5,  initialStatus: "on", initialOutput: 150 }),
    makeGenerator({ id: "g3", name: "Peaker",   type: "gas-peaker", pMin: 20,  pMax: 150, cost: 140, rampRate: 60, startupTime: 2,  initialStatus: "off" }),
  ];
  state.loads = [makeLoad("l1", "City", 500)];
  return {
    state, constants: DEFAULT_CONSTANTS, winSeconds: 120, budgetUSD: 52000,
    events: [
      { t: 40, label: "Industrial load comes online", apply: (c) => { if (c.state.loads[0]) c.state.loads[0].baseDemand = 640; } },
      { t: 80, label: "Evening peak",                  apply: (c) => { if (c.state.loads[0]) c.state.loads[0].baseDemand = 720; } },
    ],
  };
}

// ============== Layer 3 ==============

const DAY_MINUTES = 240;
function diurnal(m: number): number {
  const hod = ((m / DAY_MINUTES) * 24) % 24;
  const morning = sigmoid(hod - 6) - sigmoid(hod - 10);
  const evening = 0.6 * (sigmoid(hod - 17) - sigmoid(hod - 22));
  return 0.7 + 0.5 * morning + evening;
}
function sigmoid(x: number): number { return 1 / (1 + Math.exp(-x)); }

function level3(): GameContext {
  const state = emptyGrid();
  state.generators = [
    makeGenerator({ id: "g1", name: "Coal-1",  type: "coal",       pMin: 150, pMax: 600, cost: 30,  rampRate: 6,  startupTime: 30, initialStatus: "on", initialOutput: 400 }),
    makeGenerator({ id: "g2", name: "Gas-CCGT",type: "gas-ccgt",   pMin: 80,  pMax: 300, cost: 55,  rampRate: 25, startupTime: 5,  initialStatus: "on", initialOutput: 120 }),
    makeGenerator({ id: "g3", name: "Peaker-A",type: "gas-peaker", pMin: 20,  pMax: 150, cost: 135, rampRate: 60, startupTime: 2,  initialStatus: "off" }),
    makeGenerator({ id: "g4", name: "Peaker-B",type: "gas-peaker", pMin: 20,  pMax: 150, cost: 145, rampRate: 60, startupTime: 2,  initialStatus: "off" }),
  ];
  state.loads = [makeLoad("l1", "Region", 500, diurnal)];
  return {
    state, constants: DEFAULT_CONSTANTS, winSeconds: 240, budgetUSD: 70000, events: [],
  };
}

// ============== Layer 4: The Network ==============

function level4(): GameContext {
  const state = emptyGrid();
  state.generators = [
    makeGenerator({ id: "g1", name: "Coal-N",   type: "coal",       pMin: 150, pMax: 600, cost: 28,  rampRate: 8,  startupTime: 30, initialStatus: "on", initialOutput: 400 }),
    makeGenerator({ id: "g2", name: "Gas-S",    type: "gas-ccgt",   pMin: 100, pMax: 500, cost: 62,  rampRate: 25, startupTime: 5,  initialStatus: "on", initialOutput: 200 }),
    makeGenerator({ id: "g3", name: "Peaker-S", type: "gas-peaker", pMin: 20,  pMax: 150, cost: 145, rampRate: 60, startupTime: 2,  initialStatus: "off" }),
  ];
  state.loads = [makeLoad("l1", "South-City", 750)];
  state.network = makeNetwork({
    buses: [makeBus("bN", "North"), makeBus("bS", "South")],
    lines: [
      makeLine({ id: "L1", from: "bN", to: "bS", susceptance: 200, thermalLimit: 250 }),
      makeLine({ id: "L2", from: "bN", to: "bS", susceptance: 200, thermalLimit: 250 }),
    ],
    slackBus: "bS",
    genBus: { g1: "bN", g2: "bS", g3: "bS" },
    loadBus: { l1: "bS" },
  });
  return {
    state, constants: DEFAULT_CONSTANTS, winSeconds: 120, budgetUSD: 90000,
    events: [
      { t: 50, label: "Evening peak in the south", apply: (c) => { if (c.state.loads[0]) c.state.loads[0].baseDemand = 900; } },
    ],
  };
}

// ============== Layer 5: N-1 contingency ==============

function level5(): GameContext {
  const state = emptyGrid();
  state.generators = [
    makeGenerator({ id: "g1", name: "Coal-N",   type: "coal",       pMin: 150, pMax: 600, cost: 28,  rampRate: 8,  startupTime: 30, initialStatus: "on", initialOutput: 380 }),
    makeGenerator({ id: "g2", name: "Gas-S",    type: "gas-ccgt",   pMin: 100, pMax: 500, cost: 62,  rampRate: 25, startupTime: 5,  initialStatus: "on", initialOutput: 250 }),
    makeGenerator({ id: "g3", name: "Peaker-S", type: "gas-peaker", pMin: 20,  pMax: 150, cost: 145, rampRate: 60, startupTime: 2,  initialStatus: "off" }),
  ];
  state.loads = [makeLoad("l1", "South-City", 700)];
  state.network = makeNetwork({
    buses: [makeBus("bN", "North"), makeBus("bS", "South")],
    lines: [
      makeLine({ id: "L1", from: "bN", to: "bS", susceptance: 200, thermalLimit: 300 }),
      makeLine({ id: "L2", from: "bN", to: "bS", susceptance: 200, thermalLimit: 300 }),
    ],
    slackBus: "bS",
    genBus: { g1: "bN", g2: "bS", g3: "bS" },
    loadBus: { l1: "bS" },
  });
  return {
    state, constants: DEFAULT_CONSTANTS, winSeconds: 150, budgetUSD: 120000,
    blackoutToleranceMin: 4,
    events: [
      {
        t: 60,
        label: "⚡ Fault: Line L2 has tripped!",
        apply: (c) => {
          const l = c.state.network!.lines.find((x) => x.id === "L2");
          if (l) { l.status = "tripped"; l.trippedAt = c.state.gameMinutes; }
        },
      },
    ],
  };
}

// ============== Layer 6: Renewables ==============

function level6(): GameContext {
  const state = emptyGrid();
  state.generators = [
    makeGenerator({ id: "g1", name: "Coal-N",   type: "coal",       pMin: 150, pMax: 500, cost: 32,  rampRate: 8,  startupTime: 30, initialStatus: "on", initialOutput: 300 }),
    makeGenerator({ id: "g2", name: "Gas-S",    type: "gas-ccgt",   pMin: 80,  pMax: 400, cost: 60,  rampRate: 25, startupTime: 5,  initialStatus: "on", initialOutput: 150 }),
    makeGenerator({ id: "g3", name: "Peaker-S", type: "gas-peaker", pMin: 20,  pMax: 200, cost: 140, rampRate: 60, startupTime: 2,  initialStatus: "off" }),
    makeGenerator({
      id: "g4", name: "Wind-N", type: "wind",
      pMin: 0, pMax: 300, cost: 0, rampRate: 300, startupTime: 0,
      initialStatus: "on", initialOutput: 100,
      availabilityCurve: (m) => {
        // Slow sine + noise; clamp 0.1..0.95
        const base = 0.5 + 0.3 * Math.sin(m / 30);
        const noise = 0.15 * Math.sin(m / 4.2 + 1.7) * Math.cos(m / 7.3);
        return Math.max(0.05, Math.min(0.95, base + noise));
      },
    }),
    makeGenerator({
      id: "g5", name: "Solar-S", type: "solar",
      pMin: 0, pMax: 350, cost: 0, rampRate: 350, startupTime: 0,
      initialStatus: "on", initialOutput: 0,
      availabilityCurve: (m) => {
        // Bell curve peaking at hour 13 of compressed day
        const hod = ((m / DAY_MINUTES) * 24) % 24;
        const a = Math.exp(-((hod - 13) ** 2) / 8);
        return Math.max(0, Math.min(0.95, a));
      },
    }),
  ];
  state.loads = [makeLoad("l1", "Region", 550, diurnal)];
  state.network = makeNetwork({
    buses: [makeBus("bN", "North"), makeBus("bS", "South")],
    lines: [
      makeLine({ id: "L1", from: "bN", to: "bS", susceptance: 200, thermalLimit: 400 }),
      makeLine({ id: "L2", from: "bN", to: "bS", susceptance: 200, thermalLimit: 400 }),
    ],
    slackBus: "bS",
    genBus: { g1: "bN", g2: "bS", g3: "bS", g4: "bN", g5: "bS" },
    loadBus: { l1: "bS" },
  });
  return {
    state, constants: DEFAULT_CONSTANTS, winSeconds: 240, budgetUSD: 50000, events: [],
  };
}

// ============== Layer 7: Storage ==============

function level7(): GameContext {
  const state = emptyGrid();
  state.generators = [
    makeGenerator({ id: "g1", name: "Coal-N",   type: "coal",       pMin: 150, pMax: 500, cost: 32,  rampRate: 8,  startupTime: 30, initialStatus: "on", initialOutput: 280 }),
    makeGenerator({ id: "g2", name: "Gas-S",    type: "gas-ccgt",   pMin: 80,  pMax: 350, cost: 65,  rampRate: 25, startupTime: 5,  initialStatus: "on", initialOutput: 100 }),
    makeGenerator({ id: "g3", name: "Peaker-S", type: "gas-peaker", pMin: 20,  pMax: 200, cost: 150, rampRate: 60, startupTime: 2,  initialStatus: "off" }),
    makeGenerator({
      id: "g4", name: "Solar-S", type: "solar",
      pMin: 0, pMax: 400, cost: 0, rampRate: 400, startupTime: 0,
      initialStatus: "on", initialOutput: 0,
      availabilityCurve: (m) => {
        const hod = ((m / DAY_MINUTES) * 24) % 24;
        return Math.max(0, Math.min(0.95, Math.exp(-((hod - 13) ** 2) / 8)));
      },
    }),
    makeGenerator({
      id: "g5", name: "Battery-S", type: "battery",
      pMin: 0, pMax: 150, cost: 0, rampRate: 150, startupTime: 0,
      initialStatus: "on", initialOutput: 0,
      storageCapacity: 600, initialSoC: 100, chargeEfficiency: 0.9,
    }),
  ];
  state.loads = [makeLoad("l1", "Region", 500, diurnal)];
  state.network = makeNetwork({
    buses: [makeBus("bN", "North"), makeBus("bS", "South")],
    lines: [
      makeLine({ id: "L1", from: "bN", to: "bS", susceptance: 200, thermalLimit: 400 }),
      makeLine({ id: "L2", from: "bN", to: "bS", susceptance: 200, thermalLimit: 400 }),
    ],
    slackBus: "bS",
    genBus: { g1: "bN", g2: "bS", g3: "bS", g4: "bS", g5: "bS" },
    loadBus: { l1: "bS" },
  });
  return {
    state, constants: DEFAULT_CONSTANTS, winSeconds: 240, budgetUSD: 42000, events: [],
  };
}

// ============== Layer 8: Cascade + black start ==============

function level8(): GameContext {
  const state = emptyGrid();
  state.generators = [
    makeGenerator({ id: "g1", name: "Coal-N",   type: "coal",       pMin: 150, pMax: 600, cost: 30,  rampRate: 8,  startupTime: 30, initialStatus: "on", initialOutput: 520 }),
    makeGenerator({ id: "g2", name: "Gas-C",    type: "gas-ccgt",   pMin: 80,  pMax: 400, cost: 60,  rampRate: 25, startupTime: 5,  initialStatus: "on", initialOutput: 280, blackStartCapable: false }),
    makeGenerator({ id: "g3", name: "Peaker-S", type: "gas-peaker", pMin: 20,  pMax: 200, cost: 140, rampRate: 60, startupTime: 2,  initialStatus: "off", blackStartCapable: false }),
    makeGenerator({ id: "g4", name: "Hydro-W",  type: "hydro",      pMin: 0,   pMax: 250, cost: 15,  rampRate: 100, startupTime: 1,  initialStatus: "on", initialOutput: 100, blackStartCapable: true }),
  ];
  state.loads = [
    makeLoad("l1", "North-City",  300),
    makeLoad("l2", "Central-Ind", 400),
    makeLoad("l3", "South-Town",  200),
  ];
  state.network = makeNetwork({
    buses: [makeBus("bN", "North"), makeBus("bC", "Central"), makeBus("bS", "South"), makeBus("bW", "West-Hydro")],
    lines: [
      makeLine({ id: "L1", from: "bN", to: "bC", susceptance: 200, thermalLimit: 350 }),
      makeLine({ id: "L2", from: "bC", to: "bS", susceptance: 200, thermalLimit: 280 }),
      makeLine({ id: "L3", from: "bW", to: "bC", susceptance: 200, thermalLimit: 220 }),
      makeLine({ id: "L4", from: "bN", to: "bS", susceptance: 150, thermalLimit: 200 }),
    ],
    slackBus: "bC",
    genBus: { g1: "bN", g2: "bC", g3: "bS", g4: "bW" },
    loadBus: { l1: "bN", l2: "bC", l3: "bS" },
  });
  return {
    state, constants: DEFAULT_CONSTANTS, winSeconds: 120, budgetUSD: 200000,
    blackoutToleranceMin: 30,
    restorationTarget: 0.8,
    events: [
      {
        t: 5,
        label: "⚡ Initial fault: Line L1 trips!",
        apply: (c) => {
          const l = c.state.network!.lines.find((x) => x.id === "L1");
          if (l) { l.status = "tripped"; l.trippedAt = c.state.gameMinutes; }
        },
      },
    ],
    customOutcome: (c) => {
      // Win when 80%+ of original demand is being served and grid is stable
      const totalDemand = c.state.loads.reduce((s, l) => s + l.baseDemand, 0);
      const liveDemand = c.state.loads.reduce((s, l) => {
        if (l.shed) return s;
        const bus = c.state.network?.loadBus.get(l.id);
        if (bus && c.state.islandedBuses?.includes(bus)) return s;
        return s + l.baseDemand;
      }, 0);
      c.state.restorationProgress = liveDemand / totalDemand;
      if (liveDemand / totalDemand >= (c.restorationTarget ?? 0.8)
          && c.state.frequency >= c.constants.bandLow
          && c.state.frequency <= c.constants.bandHigh
          && c.state.gameMinutes > 20) {
        return { status: "won" };
      }
      return "";
    },
  };
}

// ============== Layer 9: LMP ==============

function level9(): GameContext {
  const state = emptyGrid();
  state.generators = [
    makeGenerator({ id: "g1", name: "Coal-N",   type: "coal",       pMin: 100, pMax: 500, cost: 28, rampRate: 8,  startupTime: 30, initialStatus: "on", initialOutput: 300 }),
    makeGenerator({ id: "g2", name: "Gas-S",    type: "gas-ccgt",   pMin: 80,  pMax: 400, cost: 60, rampRate: 25, startupTime: 5,  initialStatus: "on", initialOutput: 200 }),
    makeGenerator({ id: "g3", name: "Peaker-S", type: "gas-peaker", pMin: 20,  pMax: 200, cost: 140,rampRate: 60, startupTime: 2,  initialStatus: "off" }),
  ];
  state.loads = [makeLoad("l1", "South-City", 700)];
  state.network = makeNetwork({
    buses: [makeBus("bN", "North"), makeBus("bS", "South")],
    lines: [
      makeLine({ id: "L1", from: "bN", to: "bS", susceptance: 200, thermalLimit: 250 }),
      makeLine({ id: "L2", from: "bN", to: "bS", susceptance: 200, thermalLimit: 250 }),
    ],
    slackBus: "bS",
    genBus: { g1: "bN", g2: "bS", g3: "bS" },
    loadBus: { l1: "bS" },
  });
  state.lmp = new Map([["bN", 0], ["bS", 0]]);
  state.revenue = 0;
  const revenueTarget = 60000;
  return {
    state, constants: DEFAULT_CONSTANTS, winSeconds: 150, events: [],
    customOutcome: (c, dtMin) => {
      // Compute simple LMPs
      const onGens = c.state.generators.filter((g) => g.status === "on" && g.output > 0.5);
      onGens.sort((a, b) => a.cost - b.cost);
      // Find marginal cost at each bus
      const lmp = new Map<string, number>();
      const lines = c.state.network!.lines;
      const isCongested = lines.some((l) => l.status === "ok" && Math.abs(l.flow) > l.thermalLimit * 0.9);
      if (!isCongested) {
        // System-wide marginal cost: highest cost gen that's running below max
        const marginal = onGens.find((g) => g.output < g.pMax * g.availability - 5);
        const lambda = marginal?.cost ?? (onGens[onGens.length - 1]?.cost ?? 50);
        for (const b of c.state.network!.buses) lmp.set(b.id, lambda);
      } else {
        // Congested: prices split. Higher at importing side (more expensive local gen sets price)
        // North marginal: cheapest gen on North bus
        for (const b of c.state.network!.buses) {
          const local = onGens.filter((g) => c.state.network!.genBus.get(g.id) === b.id);
          const marginal = local.find((g) => g.output < g.pMax * g.availability - 5);
          lmp.set(b.id, marginal?.cost ?? local[local.length - 1]?.cost ?? 50);
        }
      }
      c.state.lmp = lmp;

      // Revenue accrual: sum over gens of (LMP_at_bus * output - cost * output) * dtHours
      const dtHours = dtMin / 60;
      let dRev = 0;
      for (const g of onGens) {
        const bus = c.state.network!.genBus.get(g.id);
        const price: number = (bus ? lmp.get(bus) : undefined) ?? 0;
        dRev += (price - g.cost) * g.output * dtHours;
      }
      c.state.revenue = (c.state.revenue ?? 0) + dRev;

      if ((c.state.revenue ?? 0) >= revenueTarget) {
        return { status: "won" };
      }
      return "";
    },
  };
}

// ============== Layer registry + layouts ==============

export const LEVELS: LevelDef[] = [
  {
    id: 1, layer: 1, available: true,
    title: "Keep the Lights On", subtitle: "Layer 1 · Real-time balance",
    briefing: [
      "Supply and demand must match every second.",
      "If supply > demand, frequency rises. If demand > supply, it falls.",
      "Keep frequency in the green band (49.8–50.2 Hz) for 90 seconds.",
      "Leave the trip band (48.5–51.5 Hz) and the grid blacks out.",
    ],
    build: () => {
      layout(1, [
        { id: "g1", kind: "gen", x: 0.18, y: 0.30 },
        { id: "g2", kind: "gen", x: 0.18, y: 0.55 },
        { id: "g3", kind: "gen", x: 0.18, y: 0.80 },
        { id: "l1", kind: "load", x: 0.80, y: 0.55 },
      ]);
      return level1();
    },
  },
  {
    id: 2, layer: 2, available: true,
    title: "Cheapest First", subtitle: "Layer 2 · Merit order",
    briefing: [
      "Every generator has a marginal cost ($/MWh).",
      "Dispatch cheapest first; bring expensive peakers on only when needed.",
      "Keep frequency in band AND finish under budget ($52k).",
      "Coal ramps slowly (6 MW/min) — anticipate load changes.",
    ],
    build: () => {
      layout(2, [
        { id: "g1", kind: "gen", x: 0.18, y: 0.30 },
        { id: "g2", kind: "gen", x: 0.18, y: 0.55 },
        { id: "g3", kind: "gen", x: 0.18, y: 0.80 },
        { id: "l1", kind: "load", x: 0.80, y: 0.55 },
      ]);
      return level2();
    },
  },
  {
    id: 3, layer: 3, available: true,
    title: "A Day in the Life", subtitle: "Layer 3 · Daily demand curve",
    briefing: [
      "Demand swings on a 24-hour cycle: night trough, morning ramp, evening peak.",
      "Plan ahead — coal needs 30 game-minutes to start. Peakers spin up in 2.",
      "Survive one game-day (~4 minutes real time) under $70k.",
    ],
    build: () => {
      layout(3, [
        { id: "g1", kind: "gen", x: 0.15, y: 0.25 },
        { id: "g2", kind: "gen", x: 0.15, y: 0.45 },
        { id: "g3", kind: "gen", x: 0.15, y: 0.65 },
        { id: "g4", kind: "gen", x: 0.15, y: 0.85 },
        { id: "l1", kind: "load", x: 0.82, y: 0.55 },
      ]);
      return level3();
    },
  },
  {
    id: 4, layer: 4, available: true,
    title: "The Network", subtitle: "Layer 4 · Transmission constraints",
    briefing: [
      "Power physically flows between buses via transmission lines.",
      "Each line has a thermal limit. Sustained overload → the line trips.",
      "Cheap coal is in the North; the city is in the South — but transmission is constrained.",
      "Even if North-coal is cheaper, you may have to run South-gas anyway.",
    ],
    build: () => {
      layout(4, [
        { id: "bN", kind: "bus", x: 0.25, y: 0.18 },
        { id: "bS", kind: "bus", x: 0.75, y: 0.85 },
        { id: "g1", kind: "gen", x: 0.10, y: 0.30 },
        { id: "g2", kind: "gen", x: 0.55, y: 0.78 },
        { id: "g3", kind: "gen", x: 0.55, y: 0.92 },
        { id: "l1", kind: "load", x: 0.92, y: 0.85 },
      ]);
      return level4();
    },
  },
  {
    id: 5, layer: 5, available: true,
    title: "N-Minus-One", subtitle: "Layer 5 · Contingency reliability",
    briefing: [
      "A line trips mid-shift. Will your dispatch survive it?",
      "If a parallel line carries the lost flow, it can overload too — cascade.",
      "Pre-position: don't rely on more transmission than survives the worst single failure.",
      "Customers tolerate ~4 minutes without power before this counts as a blackout.",
    ],
    build: () => {
      layout(5, [
        { id: "bN", kind: "bus", x: 0.25, y: 0.18 },
        { id: "bS", kind: "bus", x: 0.75, y: 0.85 },
        { id: "g1", kind: "gen", x: 0.10, y: 0.30 },
        { id: "g2", kind: "gen", x: 0.55, y: 0.78 },
        { id: "g3", kind: "gen", x: 0.55, y: 0.92 },
        { id: "l1", kind: "load", x: 0.92, y: 0.85 },
      ]);
      return level5();
    },
  },
  {
    id: 6, layer: 6, available: true,
    title: "Free Fuel, Fickle Wind", subtitle: "Layer 6 · Renewables",
    briefing: [
      "Wind (▲) and solar (▲) cost nothing per MWh but you can't dispatch them.",
      "Their output is set by weather/time of day, not by you.",
      "When wind drops or the sun sets, dispatchable thermals must cover the gap.",
      "Budget rewards using free fuel — but only if you can keep frequency stable.",
    ],
    build: () => {
      layout(6, [
        { id: "bN", kind: "bus", x: 0.28, y: 0.18 },
        { id: "bS", kind: "bus", x: 0.72, y: 0.82 },
        { id: "g1", kind: "gen", x: 0.10, y: 0.30 },
        { id: "g4", kind: "gen", x: 0.10, y: 0.10 },
        { id: "g2", kind: "gen", x: 0.55, y: 0.70 },
        { id: "g3", kind: "gen", x: 0.55, y: 0.88 },
        { id: "g5", kind: "gen", x: 0.92, y: 0.65 },
        { id: "l1", kind: "load", x: 0.92, y: 0.92 },
      ]);
      return level6();
    },
  },
  {
    id: 7, layer: 7, available: true,
    title: "Time-Shift", subtitle: "Layer 7 · Storage",
    briefing: [
      "The battery (■) can charge (negative MW) or discharge (positive MW).",
      "Charge when supply > demand and the sun is making free energy.",
      "Discharge at the evening peak so you don't have to burn the peaker.",
      "Capacity is limited — use it strategically.",
    ],
    build: () => {
      layout(7, [
        { id: "bN", kind: "bus", x: 0.28, y: 0.20 },
        { id: "bS", kind: "bus", x: 0.72, y: 0.82 },
        { id: "g1", kind: "gen", x: 0.10, y: 0.30 },
        { id: "g2", kind: "gen", x: 0.55, y: 0.62 },
        { id: "g3", kind: "gen", x: 0.55, y: 0.78 },
        { id: "g4", kind: "gen", x: 0.55, y: 0.94 },
        { id: "g5", kind: "gen", x: 0.90, y: 0.55 },
        { id: "l1", kind: "load", x: 0.92, y: 0.92 },
      ]);
      return level7();
    },
  },
  {
    id: 8, layer: 8, available: true,
    title: "Cascade", subtitle: "Layer 8 · Blackout & black start",
    briefing: [
      "A line trips and the rest of the network must absorb its flow.",
      "If it cascades, you lose the grid — restore at least 80% of demand to win.",
      "Only the Hydro unit is black-start capable: it can come on from cold without grid power.",
      "Bring loads back in stages so each step is balanced.",
    ],
    build: () => {
      layout(8, [
        { id: "bN", kind: "bus", x: 0.30, y: 0.20 },
        { id: "bC", kind: "bus", x: 0.60, y: 0.50 },
        { id: "bS", kind: "bus", x: 0.85, y: 0.80 },
        { id: "bW", kind: "bus", x: 0.20, y: 0.75 },
        { id: "g1", kind: "gen", x: 0.15, y: 0.10 },
        { id: "g2", kind: "gen", x: 0.55, y: 0.35 },
        { id: "g3", kind: "gen", x: 0.78, y: 0.92 },
        { id: "g4", kind: "gen", x: 0.05, y: 0.80 },
        { id: "l1", kind: "load", x: 0.42, y: 0.10 },
        { id: "l2", kind: "load", x: 0.78, y: 0.45 },
        { id: "l3", kind: "load", x: 0.95, y: 0.65 },
      ]);
      return level8();
    },
  },
  {
    id: 9, layer: 9, available: true,
    title: "The Market", subtitle: "Layer 9 · Locational marginal pricing",
    briefing: [
      "When no line is congested, the price is the same at every bus.",
      "When a line is at its limit, prices split — high at the importing bus, low at the exporting bus.",
      "Earn (LMP − cost) × MW for each generator you run. Hit $60k margin to win.",
    ],
    build: () => {
      layout(9, [
        { id: "bN", kind: "bus", x: 0.30, y: 0.22 },
        { id: "bS", kind: "bus", x: 0.70, y: 0.82 },
        { id: "g1", kind: "gen", x: 0.12, y: 0.30 },
        { id: "g2", kind: "gen", x: 0.55, y: 0.75 },
        { id: "g3", kind: "gen", x: 0.55, y: 0.92 },
        { id: "l1", kind: "load", x: 0.90, y: 0.85 },
      ]);
      return level9();
    },
  },
];
